import api from './api';
import { Song, SongMetadata } from '../types/music';

export interface SongResponse {
  song: Song;
  message?: string;
}

export interface SongsResponse {
  songs: SongMetadata[];
  total: number;
  page: number;
}

export interface SearchResponse {
  songs: SongMetadata[];
  total: number;
  query: string;
}

export interface SyncResponse {
  message: string;
  results: {
    created: SongMetadata[];
    updated: SongMetadata[];
    errors: Array<{ _id: string; error: string }>;
  };
}

class SongService {
  // Create a new song
  async createSong(
    song: Omit<Song, '_id' | 'createdAt' | 'modifiedAt' | 'createdBy'>,
  ): Promise<Song> {
    try {
      const response = await api.post<SongResponse>('/api/songs', song);
      return response.data.song;
    } catch (error) {
      console.error('Failed to create song:', error);
      throw new Error('Failed to create song');
    }
  }

  // Get all user's songs
  async getUserSongs(
    options: {
      page?: number;
      limit?: number;
      libraryType?: 'personal' | 'group';
      groupId?: string;
    } = {},
  ): Promise<SongsResponse> {
    try {
      const params = new URLSearchParams();
      if (options.page) params.append('page', options.page.toString());
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.libraryType)
        params.append('libraryType', options.libraryType);
      if (options.groupId) params.append('groupId', options.groupId);

      const response = await api.get<SongsResponse>(
        `/api/songs?${params.toString()}`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user songs:', error);
      throw new Error('Failed to fetch songs');
    }
  }

  // Get a specific song
  async getSong(songId: string): Promise<Song> {
    try {
      const response = await api.get<SongResponse>(`/api/songs/${songId}`);
      return response.data.song;
    } catch (error) {
      console.error('Failed to fetch song:', error);
      throw new Error('Failed to fetch song');
    }
  }

  // Update a song
  async updateSong(songId: string, updates: Partial<Song>): Promise<Song> {
    try {
      const response = await api.put<SongResponse>(`/api/songs/${songId}`, {
        ...updates,
        isLocalDraft: false,
      });
      return response.data.song;
    } catch (error) {
      console.error('Failed to update song:', error);
      throw new Error('Failed to update song');
    }
  }

  // Save a complete song (create or update)
  async saveSong(song: Song): Promise<Song> {
    try {
      // If song has a valid ObjectId, update it
      if (song._id && !song._id.startsWith('local_')) {
        return await this.updateSong(song._id, {
          ...song,
          isLocalDraft: false,
        });
      } else {
        // Create new song (remove local ID)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { _id: _, ...songData } = song;
        return await this.createSong({
          ...songData,
          isLocalDraft: false,
        });
      }
    } catch (error) {
      console.error('Failed to save song:', error);
      throw new Error('Failed to save song');
    }
  }

  // Delete a song
  async deleteSong(songId: string): Promise<void> {
    try {
      await api.delete(`/api/songs/${songId}`);
    } catch (error) {
      console.error('Failed to delete song:', error);
      throw new Error('Failed to delete song');
    }
  }

  // Search songs
  async searchSongs(
    query: string,
    limit: number = 20,
    options: {
      libraryType?: 'personal' | 'group';
      groupId?: string;
    } = {},
  ): Promise<SearchResponse> {
    try {
      const params = new URLSearchParams({
        q: query,
        limit: limit.toString(),
      });
      if (options.libraryType) {
        params.append('libraryType', options.libraryType);
      }
      if (options.groupId) {
        params.append('groupId', options.groupId);
      }

      const response = await api.get<SearchResponse>(
        `/api/songs/search?${params.toString()}`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to search songs:', error);
      throw new Error('Failed to search songs');
    }
  }

  // Sync multiple songs (for offline sync)
  async syncSongs(songs: Song[]): Promise<SyncResponse> {
    try {
      const response = await api.post<SyncResponse>('/api/songs/sync', {
        songs,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to sync songs:', error);
      throw new Error('Failed to sync songs');
    }
  }

  // Check if we're online/can reach the API
  async checkConnection(): Promise<boolean> {
    try {
      await api.get('/health');
      return true;
    } catch (error) {
      console.log('Backend connection failed:', error);
      return false;
    }
  }
}

const songService = new SongService();
export default songService;
